<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import Button from 'dashboard/components-next/button/Button.vue';
import { isValidPassword } from 'shared/helpers/Validators';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });
const password = ref('');
const passwordConfirmation = ref('');

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const passwordRequirements = computed(() => {
  const currentPassword = password.value || '';
  return {
    length: currentPassword.length >= 6,
    uppercase: /[A-Z]/.test(currentPassword),
    lowercase: /[a-z]/.test(currentPassword),
    number: /[0-9]/.test(currentPassword),
    special: /[!@#$%^&*()_+\-=[\]{}|'"/\\.,`<>:;?~]/.test(currentPassword),
  };
});

const passwordRequirementItems = computed(() => [
  {
    id: 'length',
    met: passwordRequirements.value.length,
    label: t('REGISTER.PASSWORD.REQUIREMENTS_LENGTH', { min: 6 }),
  },
  {
    id: 'uppercase',
    met: passwordRequirements.value.uppercase,
    label: t('REGISTER.PASSWORD.REQUIREMENTS_UPPERCASE'),
  },
  {
    id: 'lowercase',
    met: passwordRequirements.value.lowercase,
    label: t('REGISTER.PASSWORD.REQUIREMENTS_LOWERCASE'),
  },
  {
    id: 'number',
    met: passwordRequirements.value.number,
    label: t('REGISTER.PASSWORD.REQUIREMENTS_NUMBER'),
  },
  {
    id: 'special',
    met: passwordRequirements.value.special,
    label: t('REGISTER.PASSWORD.REQUIREMENTS_SPECIAL'),
  },
]);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};

const setPassword = async () => {
  if (!isValidPassword(password.value) || password.value.length < 6) {
    useAlert(t('REGISTER.PASSWORD.IS_INVALID_PASSWORD'));
    return;
  }

  if (password.value !== passwordConfirmation.value) {
    useAlert(t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.ERROR'));
    return;
  }

  try {
    await store.dispatch('agents/update', {
      id: props.id,
      password: password.value,
      password_confirmation: passwordConfirmation.value,
    });
    password.value = '';
    passwordConfirmation.value = '';
    useAlert(t('SET_NEW_PASSWORD.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) || t('SET_NEW_PASSWORD.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div v-if="provider !== 'saml'" class="w-full py-2">
        <p class="mb-2 text-sm font-medium text-n-slate-12">
          {{ $t('SET_NEW_PASSWORD.TITLE') }}
        </p>
        <div class="flex flex-col gap-3">
          <label>
            {{ $t('SET_NEW_PASSWORD.PASSWORD.LABEL') }}
            <input
              v-model="password"
              type="password"
              :placeholder="$t('SET_NEW_PASSWORD.PASSWORD.PLACEHOLDER')"
            />
          </label>

          <div class="text-sm text-n-slate-11">
            <p class="mb-2">
              {{ $t('REGISTER.PASSWORD.IS_INVALID_PASSWORD') }}
            </p>
            <ul class="space-y-1 pl-4 list-disc">
              <li
                v-for="item in passwordRequirementItems"
                :key="item.id"
                :class="item.met ? 'text-n-teal-11' : 'text-n-slate-11'"
              >
                {{ item.label }}
              </li>
            </ul>
          </div>

          <label>
            {{ $t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.LABEL') }}
            <input
              v-model="passwordConfirmation"
              type="password"
              :placeholder="$t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.PLACEHOLDER')"
            />
          </label>
          <p
            v-if="passwordConfirmation && password !== passwordConfirmation"
            class="text-sm text-n-ruby-11"
          >
            {{ $t('SET_NEW_PASSWORD.CONFIRM_PASSWORD.ERROR') }}
          </p>

          <div>
            <Button
              type="button"
              slate
              :label="$t('SET_NEW_PASSWORD.SUBMIT')"
              :disabled="
                !password ||
                !passwordConfirmation ||
                password !== passwordConfirmation ||
                !passwordRequirements.length ||
                !isValidPassword(password) ||
                uiFlags.isUpdating
              "
              :is-loading="uiFlags.isUpdating"
              @click.prevent="setPassword"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
